import type { Module } from 'vuex';
import { IRelatedProduct } from '../../entities/related-product-entity';
import { IProduct, ProductsLoadMode } from '../../entities/product-entity';

export const namespace = 'products';

export type PageSize = 24 | 48 | 72 | 96;

export interface ISortOption {
	id: string;
	type: string;
	value: string;
}

export type SortOptionsList = ISortOption[];

interface ISort {
	sortBy?: string;
	sortOrder?: string;
}
interface ILoadingState {
	products: boolean;
	product: boolean;
}

interface IPagination {
	page?: number;
	perPage?: number;
}

export interface IGetProductsParams extends IPagination, ISort {
	slug: string;
	sort?: string;
	filters?: string[];
	extensions?: string[];
	mixin?: any;
}
export interface IGetProductParams {
	slug: string;
	extensions?: string[];
}
export interface IRelatedProductParams {
	slug?: string;
	extensions?: string[];
}

export interface IProductsState {
	loading: ILoadingState;
	filters: string[];
	error: unknown;
	products: IProduct[];
	total: number;
	pageCount: number;
	currentProduct: IProduct | null;
	pagination: IPagination;
	relatedProducts: IRelatedProduct[];
	recentlyProducts: IRelatedProduct[];
	sameProducts: IRelatedProduct[];
	sort: string;
	categorySlug: null | string;
	promotionSlug: null | string;
	brandSlug: null | string;
	loadMode: ProductsLoadMode;
}

export const MutationType = {
	SET_LOAD_MODE: 'setLoadMode',
	SET_SORT: 'setSort',
	SET_CATEGORY_SLUG: 'setCategorySlug',
	SET_PRODUCTS: 'setProducts',
	SET_CURRENT_PRODUCT: 'setCurrentProduct',
	SET_PAGINATION: 'setPagination',
	SET_LOADING: 'setLoading',
	SET_TOTAL: 'setTotal',
	SET_PAGE_COUNT: 'setPageCount',
	SET_ERROR: 'setError',
	SET_FILTERS: 'setFilters',
	SET_RELATED_PRODUCTS: 'setRelatedProducts',
	SET_SAME_PRODUCTS: 'setSameProducts',
	SET_RECENTLY_PRODUCTS: 'setRecentlyProducts',
};

export const actionType = {
	RESET_STATE: 'resetState',
	GET_PRODUCTS: 'getProducts',
	GET_PRODUCT_BY_SLUG: 'getProduct',
	GET_RELATED_PRODUCTS: 'getRelatedProduct',
	GET_SAME_PRODUCTS: 'getSameProduct',
	GET_RECENTLY_PRODUCTS: 'getRecentlyProduct',
};

const actionGetProductsBy: any = {
	"category": "getProductsByCategory",
	"promotions": "getProductsByPromotion",
	"brands": "getProductsByBrand",
	"sets": "getProductsBySet",
};
const executeNameSlugs: any = {
	"category": "categorySlug",
	"promotions": "promotionSlug",
	"brands": "brandSlug",
	"sets": "setSlug",
};

export default function productStore(): Module<IProductsState, any> {
	return {
		namespaced: true,
		state: () => ({
			loading: {
				products: false,
				product: false,
			},
			categorySlug: null,
			promotionSlug: null,
			brandSlug: null,
			filters: [],
			currentProduct: null,
			error: null,
			products: [],
			total: 0,
			sort: 'default',
			pageCount: 0,
			loadMode: 'page',
			relatedProducts: [],
			sameProducts: [],
			recentlyProducts: [],
			pagination: {
				page: 1,
				perPage: 24,
			},
		}),
		actions: {
			resetState({ commit }) {
				commit(MutationType.SET_FILTERS, []);
				commit(MutationType.SET_CATEGORY_SLUG, null);
				commit(MutationType.SET_PRODUCTS, []);
				commit(MutationType.SET_SORT, 'default');
				commit(MutationType.SET_PAGINATION, { page: 1, perPage: 24 });
				commit(MutationType.SET_TOTAL, 0);
				commit(MutationType.SET_PAGE_COUNT, 0);
				commit(MutationType.SET_RELATED_PRODUCTS, []);
				commit(MutationType.SET_SAME_PRODUCTS, []);
			},
			async [actionType.GET_PRODUCTS]({ commit, state }, params: IGetProductsParams): Promise<IProduct[]> {
				try {
					commit('appState/setLoading', { products: true }, { root: true });
					let page = params.page || state.pagination.page || 1;
					const perPage = params.perPage || state.pagination.perPage || 24;
					const filters = params.filters || state.filters;
					const sort = params.sort || state.sort;
					const extensions = params.extensions || [];
					const mixin = params.mixin || 'category';

					const anyState: any = state;
					const slugName: string = executeNameSlugs[mixin];
					const stateSlug: any = anyState?.[slugName];

					if (stateSlug && params.slug !== stateSlug) {
						page = 1;
					}

					const requestBody: Record<string, any> = {
						filters,
						page,
						perPage,
						sort,
						[slugName]: params.slug,
						extensions,
					};

					const res = await this.$api.product?.[actionGetProductsBy[mixin || "category"]].execute(requestBody);
					if (res.isSuccess) {
						const value = res.getValue();
						const pagination = value?.pagination;
						let products = value?.products || [];
						if (page > 1) {
							if (state.loadMode === 'more') {
								products = state.loadMode === 'more' ? [...state.products, ...products] : products;
							}
							if (state.loadMode === 'more-back') {
								products = state.loadMode === 'more-back' ? [...products, ...state.products] : products;
							}
						}
						commit(MutationType.SET_LOAD_MODE, "page");
						commit(MutationType.SET_FILTERS, filters);
						commit(MutationType.SET_CATEGORY_SLUG, params.slug);
						commit(MutationType.SET_PRODUCTS, products);
						commit(MutationType.SET_SORT, sort);
						commit(MutationType.SET_PAGINATION, { page, perPage });
						commit(MutationType.SET_TOTAL, pagination?.totalCount || 0);
						commit(MutationType.SET_PAGE_COUNT, pagination?.pageCount || 0);

						return products;
					}
					const error = res.getError();
					throw new Error(`${error?.message}: ${error?.details}`);
				} catch (error) {
					commit(MutationType.SET_ERROR, error);
					return [];
				} finally {
					commit('appState/setLoading', { products: false }, { root: true });
					commit('appState/setLoading', { category: false }, { root: true });
				}
			},
			async [actionType.GET_PRODUCT_BY_SLUG]({ commit, state }, params: IGetProductParams): Promise<IProduct | undefined> {
				try {
					commit('appState/setLoading', { product: true }, { root: true });

					const requestBody: Record<string, any> = {
						slug: params.slug,
						extensions: params.extensions,
					};
					const res = await this.$api.product.getProductBySlug.execute(requestBody);
					if (res.isSuccess) {
						const value = res.getValue();
						const product = value?.product || null;
						commit(MutationType.SET_CURRENT_PRODUCT, product);
						return product;
					}
					return undefined;
				} catch (error) {
					commit(MutationType.SET_ERROR, error);
					return undefined;
				} finally {
					commit('appState/setLoading', { product: false }, { root: true });
				}
			},
			async [actionType.GET_RELATED_PRODUCTS]({ commit }, params: IRelatedProductParams): Promise<IRelatedProduct[]> {
				const {
					slug,
					extensions
				} = params;

				try {
					commit('appState/setLoading', { relatedProducts: true }, { root: true });
					const res = await this.$api.product.getRelatedProducts.execute({ slug, extensions });
					if (res.isSuccess) {
						const value = res.getValue();
						const products = value?.products || null;
						commit(MutationType.SET_RELATED_PRODUCTS, products);
						return products;
					}
					return [];
				} catch (error) {
					commit(MutationType.SET_ERROR, error);
					return [];
				} finally {
					commit('appState/setLoading', { relatedProducts: false }, { root: true });
				}
			},
			async [actionType.GET_RECENTLY_PRODUCTS]({ commit }, params: IRelatedProductParams): Promise<IRelatedProduct[]> {
				const {
					extensions
				} = params;

				try {
					commit('appState/setLoading', { recentlyProducts: true }, { root: true });
					const res = await this.$api.product.getRecentlyProducts.execute({
						extensions
					});
					if (res.isSuccess) {
						const value = res.getValue();
						const products = value?.products || null;
						commit(MutationType.SET_RECENTLY_PRODUCTS, products);
						return products;
					}
					return [];
				} catch (error) {
					commit(MutationType.SET_ERROR, error);
					return [];
				} finally {
					commit('appState/setLoading', { recentlyProducts: false }, { root: true });
				}
			},
			async [actionType.GET_SAME_PRODUCTS]({ commit }, params: IRelatedProductParams): Promise<IRelatedProduct[]> {
				const {
					slug,
					extensions
				} = params;

				try {
					commit('appState/setLoading', { sameProducts: true }, { root: true });
					const res = await this.$api.product.getSameProducts.execute({ slug, extensions });
					if (res.isSuccess) {
						const value = res.getValue();
						const products = value?.products || null;
						commit(MutationType.SET_SAME_PRODUCTS, products);
						return products;
					}
					return [];
				} catch (error) {
					commit(MutationType.SET_ERROR, error);
					return [];
				} finally {
					commit('appState/setLoading', { sameProducts: false }, { root: true });
				}
			},
		},
		mutations: {
			[MutationType.SET_TOTAL]: (state, total: number) => {
				state.total = total;
			},
			[MutationType.SET_PAGE_COUNT]: (state, count: number) => {
				state.pageCount = count;
			},
			[MutationType.SET_PRODUCTS]: (state, products: IProduct[]) => {
				state.products = products;
			},
			[MutationType.SET_LOAD_MODE]: (state, mode: ProductsLoadMode) => {
				state.loadMode = mode;
			},
			[MutationType.SET_CURRENT_PRODUCT]: (state, product: IProduct) => {
				state.currentProduct = product;
			},
			[MutationType.SET_LOADING]: (state, { key, value }: { key: keyof ILoadingState; value: boolean }) => {
				state.loading[key] = value;
			},
			[MutationType.SET_ERROR]: (state, error: unknown) => {
				state.error = error;
			},
			[MutationType.SET_PAGINATION]: (state, pagination: IPagination) => {
				state.pagination = {
					...state.pagination,
					...pagination,
				};
			},
			[MutationType.SET_FILTERS]: (state, filters: string[]) => {
				state.filters = filters;
			},
			[MutationType.SET_SORT]: (state, sort: string) => {
				state.sort = sort;
			},
			[MutationType.SET_CATEGORY_SLUG]: (state, slug: string) => {
				state.categorySlug = slug;
				state.promotionSlug = slug;
				state.brandSlug = slug;
			},
			[MutationType.SET_RELATED_PRODUCTS]: (state, products: IRelatedProduct[]) => {
				state.relatedProducts = products;
			},
			[MutationType.SET_SAME_PRODUCTS]: (state, products: IRelatedProduct[]) => {
				state.sameProducts = products;
			},
			[MutationType.SET_RECENTLY_PRODUCTS]: (state, products: IRelatedProduct[]) => {
				state.recentlyProducts = products;
			},
		},
		getters: {
			loading: ({ loading }) => loading,
			products: ({ products }) => products,
			relatedProducts: ({ relatedProducts }) => relatedProducts,
			sameProducts: ({ sameProducts }) => sameProducts,
			currentProduct: ({ currentProduct }) => currentProduct,
			recentlyProducts: ({ recentlyProducts }) => recentlyProducts,
			total: ({ total }) => total,
			pageCount: ({ pageCount }) => pageCount,
			pagination: ({ pagination }) => pagination,
			sort: ({ sort }) => sort,
			breadcrumbs: ({ currentProduct }) => {
				const breadcrumbs: any[] = [{ title: 'Главная', path: '/' }];
				if (currentProduct) {
					let path = '';
					currentProduct.categories.forEach(({ name: title, slug }) => {
						path += `/${slug}`;
						breadcrumbs.push({ title, path });
					});
				}
				return breadcrumbs;
			},
		},
	};
}
